import { Dates, OData } from "../util";
import type { ODataQueryOptions, ODataResult } from "../util";
import type { AccountBase, InvoiceListItem, PageResultOfApiListItem, PayrollListItem, SessionUserCredential, UserSession, WorkerListItem, MessageListItem, CalculationListItem, FileListItem, TaxcardListItem, HouseholdYearListItem, } from "../model";
import { PeriodType } from "../model";
import { irpsr, irepr } from "../model";
import { Ajax } from "./infra";

/**
 * Methods for PRO users (accountants, integrated services), for fetching data accross multiple accounts
 * and impersonating as different authorized acccounts: Fetching the token and session.
 */
export class ProApi {

  /**
   * For NG1-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1"];

  private routePrefix = "/v01/api/pro";

  constructor(private ajax: Ajax) {}

  /** Gets the PRO session for the current account (the PRO account) including the Service Model */
  public getProSession(): Promise<UserSession> {
    const method = `${this.routePrefix}/session`;
    return this.ajax.getJSON(method);
  }

  /** Lists all accounts on behalf of which this account has been authorized to act. */
  public getAccounts(): Promise<AccountBase[]> {
    const method = `${this.routePrefix}/accounts`;
    return this.ajax.getJSON(method);
  }

  /**
   * Set the current account as primary partner for the account.
   * @param accountId The account id for which the primary partner is set.
   * @param serviceModelId Selected service model.
   * @returns Updated account with the partner set.
   */
  public saveAccountPartner(accountId: string, serviceModelId: string): Promise<UserSession> {
    if (!accountId || !serviceModelId) {
      throw new Error("Account and service model must be provided.");
    }
    const method = `${this.routePrefix}/accounts/${encodeURIComponent(accountId)}/partner?serviceModelId=${encodeURIComponent(serviceModelId)}`;
    return this.ajax.postJSON(method, {});
  }

  /**
   * Remove the current primary partner (current account) from the account.
   * @param accountId The account id from which the primary partner is removed.
   * @returns Updated account with the partner removed.
   */
  public deleteAccountPartner(accountId: string): Promise<UserSession> {
    if (!accountId) {
      throw new Error("Account id must be provided.");
    }
    const method = `${this.routePrefix}/accounts/${encodeURIComponent(accountId)}/partner`;
    return this.ajax.remove(method);
  }

  /**
   * Lists all credentials for this partner account.
   * @returns Credentials for the partner account.
   */
  public getCredentials(): Promise<SessionUserCredential[]> {
    const method = `${this.routePrefix}/credentials`;
    return this.ajax.getJSON(method);
  }

  /**
   * Returns a new JWT token for the given account.
   * The requesting user must have access to the account.
   * @param accountId The account id for which the token is requested.
   * @returns A valid Salaxy JWT token for the given account
   */
  public getToken(accountId: string): Promise<string> {
    const method = `${this.routePrefix}/accounts/${encodeURIComponent(accountId)}/token`;
    return this.ajax.getJSON(method);
  }

  /**
   * Returns list of payrolls for the current partner (all customers).
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public oDataSearchPayrolls(query?: ODataQueryOptions | string): Promise<ODataResult<PayrollListItem>> {
    return OData.getOData<any>(`${this.routePrefix}/payrolls`, query, this.ajax);
  }

  /**
   * Returns list of invoices for the current partner (all customers).
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public oDataSearchInvoices(query?: ODataQueryOptions | string): Promise<ODataResult<InvoiceListItem>> {
    return OData.getOData<any>(`${this.routePrefix}/invoices`, query, this.ajax);
  }

  /**
   * Returns list of messages for the current partner (all customers).
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public oDataSearchMessages(query?: ODataQueryOptions | string): Promise<ODataResult<MessageListItem>> {
    return OData.getOData<any>(`${this.routePrefix}/messages`, query, this.ajax);
  }

  /**
   * Returns list of editable calculations for the current partner.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public oDataSearchEditableCalculations(query?: ODataQueryOptions | string): Promise<ODataResult<CalculationListItem>> {
    return OData.getOData<any>(`${this.routePrefix}/calculations/category/editable`, query, this.ajax);
  }

  /**
   * Returns list of readonly calculations for the current partner.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public oDataSearchReadonlyCalculations(query?: ODataQueryOptions | string): Promise<ODataResult<CalculationListItem>> {
    return OData.getOData<any>(`${this.routePrefix}/calculations/category/readonly`, query, this.ajax);
  }

  /**
   * Returns list of workers for the current partner.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public oDataSearchWorkers(query?: ODataQueryOptions | string): Promise<ODataResult<WorkerListItem>> {
    return OData.getOData<any>(`${this.routePrefix}/workers`, query, this.ajax);
  }

  /**
   * Returns list of files for the current partner.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public oDataSearchFiles(query?: ODataQueryOptions | string): Promise<ODataResult<FileListItem>> {
    return OData.getOData<any>(`${this.routePrefix}/files`, query, this.ajax);
  }

  /**
   * Returns list of taxcards for the current partner.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public oDataSearchTaxcards(query?: ODataQueryOptions | string): Promise<ODataResult<TaxcardListItem>> {
    return OData.getOData<any>(`${this.routePrefix}/taxcards`, query, this.ajax);
  }

  /**
   * Returns list of Earnings Payment Reports (EPR / Palkkatietoilmoitus)
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public oDataSearchEarningsPayments(query?: ODataQueryOptions | string): Promise<ODataResult<irepr.EarningsPaymentListItem>> {
    return OData.getOData<any>(`${this.routePrefix}/ir-epr`, query, this.ajax);
  }

  /**
   * Returns list of Payer Summary Reports (PSR / Erillisilmoitus)
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public oDataSearchPayerSummaries(query?: ODataQueryOptions | string): Promise<ODataResult<irpsr.PayerSummaryListItem>> {
    return OData.getOData<any>(`${this.routePrefix}/ir-psr`, query, this.ajax);
  }

  /**
   * Returns household yearly list items for the current partner.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public oDataSearchHouseholdYears(query?: ODataQueryOptions | string): Promise<ODataResult<HouseholdYearListItem>> {
    return OData.getOData<any>(`${this.routePrefix}/household-years`, query, this.ajax);
  }

  /**
   * Returns settings list items for the current partner.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   * TODO: Return type should be Promise<ODataResult<AccountSettingsListItem>> but AccountSettingsListItem is currently not available. See if the typing should be brought to project.
   */
  public oDataSearchAccountSettings(query?: ODataQueryOptions | string): Promise<ODataResult<any>> {
    return OData.getOData<any>(`${this.routePrefix}/settings`, query, this.ajax);
  }

  /**
   * Returns list of customers for the current partner.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   * TODO: Return type should be Promise<ODataResult<PersonListItem>>
   */
  public oDataSearchCustomers(query?: ODataQueryOptions | string): Promise<ODataResult<any>> {
    return OData.getOData<any>(`${this.routePrefix}/customers`, query, this.ajax);
  }

  /**
   * Returns list of customers for the current partner.
   * @param refDate Reference date for the period. Please note that even if the date is not the first day of the given period, the entire period is returned.
   * @param periodType Month, quarter, year or a custom period. The custom period requires endDate. Default value is the month.
   * @param endDate End date for the period. Required only for the custom period.
   * @returns List of customers for the current partner.
   */
  public searchCustomers(refDate: string, periodType: PeriodType = PeriodType.Month, endDate?: string): Promise<PageResultOfApiListItem> {
    // TODO: Return type is really PageResultOfPersonListItem, but PersonListItem is currently not available. See if the typing should be brought to project OR is this method obsolete?
    refDate = Dates.asDate(refDate);
    endDate = Dates.asDate(endDate);
    const method = `${this.routePrefix}/customers/${refDate}?periodType=${periodType || ""}&endDate=${endDate || ""}`;
    return this.ajax.getJSON(method);
  }

  // TODO: OData methods!!!

}